import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Sparkles, Feather, Loader2, Check, Play, Pause } from 'lucide-react';
import BookShell from './BookShell';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/* Sent2ment palette — mirrors VaultViewerPage / VaultEditPage. */
const C = {
  cream: '#F5F1E8',
  paper: '#FBF8F1',
  midnight: '#0B1F3A',
  gold: '#C6A96B',
  text: '#0B1F3A',
  textSec: '#5A5A5A',
  border: 'rgba(198, 169, 107, 0.25)',
};

/* Same prompt copy used in InteractiveJournalSection so the recipient sees a
   matching guided prompt. The four sender pages are intentionally LOCKED here
   and rendered as completed messages from the sender. */
const SENDER_TITLES = {
  1: 'The Beginning',
  7: 'A gentle moment',
  18: 'A quiet reminder',
  30: 'When you need it most',
};
const RECIPIENT_PROMPTS = {
  2: 'Something I love about myself',
  3: 'A memory I want to hold onto',
  4: 'A song that finds me on hard days',
  5: 'Something that makes me feel safe',
  6: 'A small thing that made me smile this week',
  8: 'A place I’d like to return to',
  9: 'Something I’ve never said out loud',
  10: 'The version of me I’m becoming',
  11: 'A lesson I’m still learning',
  12: 'Something I want to carry forward',
  13: 'A moment I would live again',
  14: 'The first time I felt understood',
  15: 'A quiet wish I’m holding',
  16: 'Three small things I needed to hear today',
  17: 'Something I’d like to forgive myself for',
  19: 'Something soft — just because',
  20: 'A truth I never want to doubt',
  21: 'A thank-you I never quite said',
  22: 'How I see my future, today',
  23: 'A line I’d write to my younger self',
  24: 'What “home” feels like to me',
  25: 'Something I’ve learned from being loved',
  26: 'A laugh I want to remember',
  27: 'Something to read on a missing-them day',
  28: 'A promise I’m keeping for myself',
  29: 'The kind of love I hope to grow into',
};
const SENDER_PAGES = new Set([1, 7, 18, 30]);

/* ── Styled "Listen to your message" pill — matches Pocket Keepsake /
   Affirmation cards so the journal voice feels like part of the brand,
   not a raw browser audio control. ── */
const ListenPill = ({ src, testid }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      const p = a.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else {
      a.pause();
    }
  };
  return (
    <div className="mb-5 flex flex-col items-start">
      <button
        type="button"
        onClick={togglePlay}
        data-testid={testid}
        aria-label={playing ? 'Pause message' : 'Listen to your message'}
        className="inline-flex items-center justify-center gap-2.5 px-5 py-3 transition-all duration-300 hover:opacity-90"
        style={{
          background: `linear-gradient(180deg, ${C.gold}28 0%, ${C.gold}3F 100%)`,
          border: `1.25px solid ${C.gold}`,
          borderRadius: '999px',
          color: C.midnight,
          letterSpacing: '0.08em',
          fontSize: '12px',
          fontWeight: 600,
          fontFamily: 'Georgia, serif',
          boxShadow: `inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -1px 0 ${C.gold}33, 0 6px 14px rgba(30, 45, 69, 0.16)`,
        }}
      >
        {playing ? <Pause size={13} strokeWidth={1.6} /> : <Play size={13} strokeWidth={1.6} style={{ marginLeft: '1px' }} />}
        {playing ? 'Pause' : 'Listen to your message'}
      </button>
      <audio
        ref={audioRef}
        src={src || undefined}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        className="sr-only"
      />
    </div>
  );
};

/* ── Sender-locked page (read-only on the recipient view).
   Page 1 also shows the sender's optional voice note (vault_extra_audio_url)
   if one was added — it lives INSIDE the journal, not as a separate
   section, so the recipient experiences the voice as part of the journal. ── */
const SenderPage = ({ pageNumber, title, message, audioUrl }) => (
  <div
    data-testid={`recipient-page-${pageNumber}`}
    data-editable="false"
    aria-readonly="true"
    className="h-full flex flex-col"
  >
    <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
      <p className="text-[10px] tracking-[0.35em] uppercase font-semibold" style={{ color: C.gold }}>
        Page {pageNumber}
      </p>
      <span
        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase italic"
        style={{ color: C.gold, fontFamily: 'Georgia, serif' }}
        data-testid={`recipient-page-${pageNumber}-sender-badge`}
      >
        <Sparkles size={11} strokeWidth={1.4} /> A message left for you
      </span>
    </div>

    <h3
      className="text-2xl sm:text-[1.6rem] font-light leading-snug mb-4"
      style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }}
    >
      {title}
    </h3>

    {audioUrl && (
      <ListenPill
        src={audioUrl}
        testid={`recipient-page-${pageNumber}-listen`}
      />
    )}

    {message ? (
      <p
        className="text-[15.5px] leading-[1.85] whitespace-pre-wrap flex-1 overflow-auto pr-1"
        style={{ color: C.text, fontFamily: 'Georgia, serif' }}
        data-testid={`recipient-page-${pageNumber}-sender-message`}
      >
        {message}
      </p>
    ) : (
      <p
        className="text-[14px] italic leading-relaxed"
        style={{ color: `${C.textSec}AA`, fontFamily: 'Georgia, serif' }}
      >
        A quiet page, kept just for you.
      </p>
    )}
  </div>
);

/* ── Recipient-editable page with auto-save ────────────────────── */
const RecipientPage = ({ pageNumber, prompt, value, onChangeDraft, status }) => (
  <div
    data-testid={`recipient-page-${pageNumber}`}
    data-editable="true"
    className="h-full flex flex-col"
  >
    <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
      <p className="text-[10px] tracking-[0.35em] uppercase font-semibold" style={{ color: C.gold }}>
        Page {pageNumber}
      </p>
      <span
        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase italic"
        style={{ color: `${C.textSec}CC`, fontFamily: 'Georgia, serif' }}
        data-testid={`recipient-page-${pageNumber}-recipient-badge`}
      >
        <Feather size={11} strokeWidth={1.4} /> Your space to write
      </span>
    </div>

    <h3
      className="text-lg sm:text-xl font-light leading-snug mb-4 italic"
      style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }}
    >
      {prompt}
    </h3>

    <textarea
      value={value || ''}
      onChange={(e) => onChangeDraft(pageNumber, e.target.value)}
      placeholder="Take your time. Write only what feels right…"
      rows={7}
      maxLength={5000}
      data-testid={`recipient-page-${pageNumber}-textarea`}
      className="w-full px-4 py-3 text-[15px] outline-none transition-colors duration-300 resize-none flex-1"
      style={{
        background: '#FCF7E6',
        color: C.text,
        border: `1px solid ${C.border}`,
        borderRadius: '6px',
        fontFamily: 'Georgia, serif',
        lineHeight: '1.7',
        minHeight: '180px',
      }}
    />

    {status === 'saving' && (
      <p className="text-[11px] mt-2 italic flex items-center gap-2" style={{ color: C.textSec }}>
        <Loader2 size={11} className="animate-spin" /> saving…
      </p>
    )}
    {status === 'saved' && (
      <p
        className="text-[11px] mt-2 italic flex items-center gap-2"
        style={{ color: C.gold }}
        data-testid={`recipient-page-${pageNumber}-saved`}
      >
        <Check size={11} strokeWidth={2} /> kept safely
      </p>
    )}
  </div>
);

/* ── Section ────────────────────────────────────────────────── */
const RecipientJournal = ({ qrCode, password, senderMessages = {}, senderAudioUrl }) => {
  // pageNumber → text. Loaded once on mount, then maintained client-side.
  const [drafts, setDrafts] = useState({});
  const [statuses, setStatuses] = useState({});  // pageNumber → 'saving' | 'saved' | 'error'
  const [, setLoaded] = useState(false);
  const timersRef = useRef({});

  // Initial load — passes the vault passcode (cached after the recipient
  // unlocked the gate). For passcode-free vaults the password is null.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/vault/${encodeURIComponent(qrCode)}/recipient-journal`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password: password || '' }),
          cache: 'no-store',
        });
        if (!res.ok) {
          if (!cancelled) setLoaded(true);
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        const incoming = {};
        Object.entries(data.pages || {}).forEach(([k, v]) => { incoming[parseInt(k, 10)] = v; });
        setDrafts(incoming);
        setLoaded(true);
      } catch {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [qrCode, password]);

  const persistPage = useCallback(async (pageNumber, content) => {
    setStatuses((s) => ({ ...s, [pageNumber]: 'saving' }));
    try {
      const res = await fetch(`${API_BASE}/api/vault/${encodeURIComponent(qrCode)}/recipient-journal/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page_number: pageNumber, content, password: password || '' }),
        cache: 'no-store',
      });
      if (res.ok) {
        setStatuses((s) => ({ ...s, [pageNumber]: 'saved' }));
        setTimeout(() => {
          setStatuses((s) => {
            if (s[pageNumber] !== 'saved') return s;
            const next = { ...s }; delete next[pageNumber]; return next;
          });
        }, 2200);
      } else {
        setStatuses((s) => ({ ...s, [pageNumber]: 'error' }));
      }
    } catch {
      setStatuses((s) => ({ ...s, [pageNumber]: 'error' }));
    }
  }, [qrCode, password]);

  const onChangeDraft = (pageNumber, text) => {
    setDrafts((d) => ({ ...d, [pageNumber]: text }));
    if (timersRef.current[pageNumber]) clearTimeout(timersRef.current[pageNumber]);
    timersRef.current[pageNumber] = setTimeout(() => persistPage(pageNumber, text), 900);
  };

  const renderPage = (pageNumber) =>
    SENDER_PAGES.has(pageNumber) ? (
      <SenderPage
        pageNumber={pageNumber}
        title={SENDER_TITLES[pageNumber]}
        message={senderMessages[pageNumber] || ''}
        audioUrl={pageNumber === 1 ? senderAudioUrl : undefined}
      />
    ) : (
      <RecipientPage
        pageNumber={pageNumber}
        prompt={RECIPIENT_PROMPTS[pageNumber]}
        value={drafts[pageNumber]}
        onChangeDraft={onChangeDraft}
        status={statuses[pageNumber]}
      />
    );

  return (
    <BookShell
      totalPages={30}
      renderPage={renderPage}
      header={{
        overline: 'Your Journal · 30 quiet pages',
        title: 'Four messages held for you. Twenty-six pages, yours to write.',
        body:
          "Pages 1, 7, 18 and 30 are messages left for you — revealed gently, when the moment feels right. Every other page is yours, to write in your own time. There’s no rush. You can come back whenever you need.",
      }}
      sectionTestId="recipient-journal-section"
      brandTestId="recipient-journal-brand-mark"
      coverTestId="recipient-journal-cover"
    />
  );
};

export default RecipientJournal;
